import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';

const getExtensao = (arquivo) => String(arquivo || '').split('.').pop().toLowerCase();

function WordTextoPreview({ texto, aviso }) {
  if (!String(texto || '').trim()) {
    return (
      <p className="modal-documento-preview-empty">
        Não foi possível extrair texto deste documento Word para visualização. Use o botão de download para abrir o arquivo no Word.
      </p>
    );
  }

  return (
    <>
      {aviso && <p className="modal-documento-preview-attribution">{aviso}</p>}
      <pre className="modal-documento-preview-texto">{texto}</pre>
    </>
  );
}

const DocumentoWordPreview = ({ arquivo, titulo, url, documentoId, textoExtraido }) => {
  const containerRef = useRef(null);
  const extensao = getExtensao(arquivo);
  const [carregando, setCarregando] = useState(true);
  const [modo, setModo] = useState(extensao === 'docx' ? 'docx' : 'texto');
  const [texto, setTexto] = useState(String(textoExtraido || '').trim());
  const [avisoTexto, setAvisoTexto] = useState('');

  useEffect(() => {
    let cancelado = false;

    async function carregarTexto() {
      if (String(textoExtraido || '').trim()) {
        if (cancelado) return;
        setTexto(String(textoExtraido).trim());
        setAvisoTexto('Texto extraído automaticamente (pode não incluir imagens ou formatação do original).');
        setModo('texto');
        setCarregando(false);
        return;
      }

      if (!documentoId) {
        if (!cancelado) setCarregando(false);
        return;
      }

      try {
        const res = await api.get(`/api/documentos/${documentoId}/preview-texto`);
        if (cancelado) return;
        const textoResposta = String(res.data?.texto || '').trim();
        setTexto(textoResposta);
        if (res.data?.fonte === 'cache') {
          setAvisoTexto('Texto extraído automaticamente (pode não incluir imagens ou formatação do original).');
        } else if (textoResposta) {
          setAvisoTexto('Visualização em texto simples — use o download para ver o layout original no Word.');
        }
        setModo('texto');
      } catch (err) {
        console.error('Erro ao carregar preview de texto Word:', err);
        if (!cancelado) setModo('texto');
      } finally {
        if (!cancelado) setCarregando(false);
      }
    }

    async function renderizarDocx() {
      const container = containerRef.current;
      if (!container) return;

      container.innerHTML = '';
      setCarregando(true);
      setModo('docx');

      try {
        const resposta = await fetch(url);
        if (!resposta.ok) throw new Error('Não foi possível carregar o arquivo Word.');

        const { renderAsync } = await import('docx-preview');
        const blob = await resposta.blob();
        if (cancelado) return;

        await renderAsync(blob, container, null, {
          className: 'docx-preview',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          breakPages: true,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true
        });

        if (!cancelado) {
          setModo('docx');
          setCarregando(false);
        }
      } catch (err) {
        console.warn('Preview docx indisponível, usando texto:', err);
        if (!cancelado) await carregarTexto();
      }
    }

    if (extensao === 'docx') {
      renderizarDocx();
    } else {
      carregarTexto();
    }

    return () => {
      cancelado = true;
    };
  }, [url, extensao, documentoId, textoExtraido]);

  return (
    <>
      {carregando && <p className="modal-documento-preview-loading">Carregando visualização do Word...</p>}
      <div
        ref={containerRef}
        className="modal-documento-preview-docx"
        aria-label={titulo || 'Documento Word'}
        style={{ display: modo === 'docx' && !carregando ? 'block' : 'none' }}
      />
      {modo === 'texto' && !carregando && <WordTextoPreview texto={texto} aviso={avisoTexto} />}
    </>
  );
};

export default DocumentoWordPreview;
